import React, { useState, useCallback, useRef, useEffect } from "react";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { IdeaConceptDisplay } from "./IdeaConceptDisplay";
import { IdeaBody, IdeaBodySectionKey, IDEA_BODY_SECTIONS } from "../types";
import { isEmptyBodyText } from "../ideaHelpers";

interface IdeaBodySectionsProps {
  body: IdeaBody;
  /** Present → sections are editable. Absent → read-only, empty sections skipped. */
  onSectionSave?: (key: IdeaBodySectionKey, value: string) => void;
  uploadFile?: (file: File) => Promise<string>;
  /** Localized label overrides (portal chromes; defaults are English). */
  labels?: Partial<Record<IdeaBodySectionKey, string>>;
}

function SectionPanel({
  sectionKey,
  label,
  value,
  isExpanded,
  onToggle,
  onSave,
  uploadFile,
}: {
  sectionKey: IdeaBodySectionKey;
  label: string;
  value: string;
  isExpanded: boolean;
  onToggle: () => void;
  onSave: (value: string) => void;
  uploadFile?: (file: File) => Promise<string>;
}) {
  const [pendingContent, setPendingContent] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const isEmpty = isEmptyBodyText(value);

  useEffect(() => {
    if (isExpanded && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isExpanded]);

  const handleSave = () => {
    if (pendingContent !== null) {
      onSave(pendingContent);
    }
    setPendingContent(null);
    onToggle();
  };

  const handleCancel = () => {
    setPendingContent(null);
    onToggle();
  };

  return (
    <div ref={panelRef} className="rounded-lg border border-gray-200 transition-colors">
      {/* Collapsed header / preview */}
      {!isExpanded && (
        <div
          onClick={onToggle}
          className={`px-3 py-2 rounded-lg cursor-pointer transition-colors hover:border-gray-300 min-h-[48px] ${
            isEmpty ? "bg-gray-50/50" : "bg-white"
          }`}
        >
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 pointer-events-none">
            {label}
          </label>
          {isEmpty ? (
            <span className="text-sm text-gray-400">Add {label.toLowerCase()}...</span>
          ) : (
            <IdeaConceptDisplay concept={value} variant="full" />
          )}
        </div>
      )}

      {/* Expanded editor */}
      {isExpanded && (
        <div className="flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50/50 rounded-t-lg">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {label}
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                className="px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                Save
              </button>
            </div>
          </div>
          <div className="min-h-[250px]">
            <RichTextEditor
              key={sectionKey}
              initialContent={value || undefined}
              onContentChange={(json) => setPendingContent(json)}
              uploadFile={uploadFile}
              className="min-h-[250px]"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export const IdeaBodySections: React.FC<IdeaBodySectionsProps> = ({
  body,
  onSectionSave,
  uploadFile,
  labels,
}) => {
  const [expandedSection, setExpandedSection] = useState<IdeaBodySectionKey | null>(null);

  const handleToggle = useCallback((key: IdeaBodySectionKey) => {
    setExpandedSection((prev) => (prev === key ? null : key));
  }, []);

  const handleSave = useCallback(
    (key: IdeaBodySectionKey, value: string) => {
      onSectionSave?.(key, value);
    },
    [onSectionSave]
  );

  // Read-only (no save handler): filled sections only, no editors, no
  // "Add ..." placeholders. Used by the client review drawer.
  if (!onSectionSave) {
    const filled = IDEA_BODY_SECTIONS.filter(({ key }) => !isEmptyBodyText(body[key]));
    if (filled.length === 0) return null;
    return (
      <div className="space-y-4">
        {filled.map(({ key, label }) => (
          <section key={key}>
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em] mb-2">
              {labels?.[key] ?? label}
            </div>
            <div className="text-sm text-gray-800 leading-relaxed">
              <IdeaConceptDisplay concept={body[key]} variant="full" />
            </div>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {IDEA_BODY_SECTIONS.map(({ key, label }) => (
        <SectionPanel
          key={key}
          sectionKey={key}
          label={labels?.[key] ?? label}
          value={body[key] || ""}
          isExpanded={expandedSection === key}
          onToggle={() => handleToggle(key)}
          onSave={(value) => handleSave(key, value)}
          uploadFile={uploadFile}
        />
      ))}
    </div>
  );
};
